// This is the file that has all the API calls
// It starts with all the necessary constants

import { getDistance } from "geolib";
import { AuthManager } from "./AuthManager";

const LAMBDA_PROXY_URL = 'https://zhhk3uoagrspnnakhqugag4u2a0tkelq.lambda-url.us-east-1.on.aws';

// Helper function to handle API calls with automatic token refresh
async function callApiWithRefresh(apiCallback, ...args) {
    try {
        // First attempt with current token
        const result = await apiCallback(...args);
        return result;
    } catch (error) {
        // If API call failed potentially due to auth issue
        if (error.message?.includes('unauthorized') || error.message?.includes('token')) {
            console.log('API call failed due to possible auth issue, attempting token refresh...');

            // Try refreshing the token
            const refreshed = await AuthManager.refreshAccessToken();

            if (refreshed) {
                // Retry the API call with the new token
                return await apiCallback(...args);
            }
        }
        // Either not an auth error or refresh failed, rethrow
        throw error;
    }
}

export async function getVehicles(center, targetDistance, selectedModels = []) {
    const COMMUNAUTO_DOMAIN = 'www.reservauto.net';
    const PATH = '/WCF/LSI/LSIBookingServiceV3.svc/GetAvailableVehicles?BranchID=1&LanguageID=1';

    try {
        const response = await fetch(
            `${LAMBDA_PROXY_URL}/?domain=${COMMUNAUTO_DOMAIN}&path=${encodeURIComponent(PATH)}`,
            {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            }
        );

        const data = await response.json();

        // Check if data exists and has vehicles
        if (!data.d?.Vehicles) {
            return [];
        }

        // Filter and remove duplicates
        let vehicles = data.d.Vehicles.filter((vehicle, index) => {
            // Filter by selected models if any are specified
            if (selectedModels && selectedModels.length > 0 && !selectedModels.includes(vehicle.CarModel)) {
                return false;
            }
            for (let i = index + 1; i < data.d.Vehicles.length; i++) {
                if (vehicle.CarVin === data.d.Vehicles[i].CarVin) {
                    return false;
                }
            }
            return true;
        });

        return vehicles.map(function (elem) {
            const coordsVehicle = {
                latitude: elem.Latitude,
                longitude: elem.Longitude,
            };
            const coordsUser = { latitude: center.lat(), longitude: center.lng() };

            var distance = getDistance(coordsVehicle, coordsUser);
            var good = distance <= targetDistance ? true : false;

            return {
                distance: Math.round(distance),
                name: elem.CarNo.toString(),
                coords: coordsVehicle,
                good: good,
                data: {
                    ...elem,
                    Id: elem.CarVin,
                    Name: elem.CarNo.toString(),
                    ModelName: elem.CarModel,
                    Position: {
                        Lat: elem.Latitude,
                        Lon: elem.Longitude
                    },
                    EnergyLevel: elem.EnergyLevel
                },
                energy: elem.EnergyLevel,
                id: elem.CarVin
            };
        });

    } catch (error) {
        return [];
    }
}

export async function bookCar(carId, token, customerId = 94823) {
    const bookCarImpl = async (carId, token, customerId) => {
        // Get valid access token (auto-refresh if expired)
        const validToken = await AuthManager.getValidAccessToken();
        if (!validToken) {
            throw new Error('Not authenticated, please log in');
        }

        const COMMUNAUTO_DOMAIN = 'www.reservauto.net';

        const PATH = `/WCF/LSI/LSIBookingServiceV3.svc/CreateBooking?CustomerID=${customerId}&CarID=${carId}`;
        const response = await fetch(
            `${LAMBDA_PROXY_URL}?path=${encodeURIComponent(PATH)}&domain=${COMMUNAUTO_DOMAIN}`,
            {
                headers: {
                    'Authorization': `Bearer ${validToken}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            }
        );

        const data = await response.json();

        if (data.d?.Success) {
            return { success: true, message: `Car ${carId} booked successfully!` };
        } else {
            if (data.d?.ErrorMessage?.toLowerCase().includes('unauthorized')) {
                throw new Error('unauthorized');
            }
            return { success: false, message: data.d?.ErrorMessage || 'Unknown booking error' };
        }
    };

    return callApiWithRefresh(bookCarImpl, carId, token, customerId);
}

export async function getCustomerId(token) {
    const getCustomerIdImpl = async (token) => {
        // Get valid access token (auto-refresh if expired)
        const validToken = await AuthManager.getValidAccessToken();
        if (!validToken) {
            throw new Error('Not authenticated, please log in');
        }

        const COMMUNAUTO_DOMAIN = 'restapifrontoffice.reservauto.net';
        const PATH = '/api/v2/Customer';

        const response = await fetch(
            `${LAMBDA_PROXY_URL}/?domain=${COMMUNAUTO_DOMAIN}&path=${encodeURIComponent(PATH)}`,
            {
                headers: {
                    'Authorization': `Bearer ${validToken}`,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                method: 'GET'
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('unauthorized');
            }
            throw new Error(`API error: ${response.statusText}`);
        }

        const data = await response.json();
        if (!data?.customerId) {
            throw new Error('Could not get customer ID');
        }
        return data.customerId;
    };

    return callApiWithRefresh(getCustomerIdImpl, token);
}

export async function getCurrentBooking(token, customerId) {
    const getCurrentBookingImpl = async (token, customerId) => {
        // Get valid access token (auto-refresh if expired)
        const validToken = await AuthManager.getValidAccessToken();
        if (!validToken) {
            throw new Error('Not authenticated, please log in');
        }

        const COMMUNAUTO_DOMAIN = 'www.reservauto.net';
        const PATH = `/WCF/LSI/LSIBookingServiceV3.svc/GetCurrentBooking?CustomerID=${customerId}&BranchID=1`;

        const response = await fetch(
            `${LAMBDA_PROXY_URL}/?domain=${COMMUNAUTO_DOMAIN}&path=${encodeURIComponent(PATH)}`,
            {
                headers: {
                    'Authorization': `Bearer ${validToken}`,
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                method: 'GET'
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('unauthorized');
            }
        }

        const data = await response.json();
        if (data.d?.Success) {
            return { success: true, message: `Booking fetched successfully!`, data: data.d.Booking };
        } else {
            if (data.d?.ErrorMessage?.toLowerCase().includes('unauthorized')) {
                throw new Error('unauthorized');
            }
            return { success: false, message: data.d?.ErrorMessage || 'Unknown booking error' };
        }
    };

    return callApiWithRefresh(getCurrentBookingImpl, token, customerId);
}

export async function apiCancelBooking(token, rentalId) {
    const apiCancelBookingImpl = async (token, rentalId) => {
        // Get valid access token (auto-refresh if expired)
        const validToken = await AuthManager.getValidAccessToken();
        if (!validToken) {
            throw new Error('Not authenticated, please log in');
        }

        const COMMUNAUTO_DOMAIN = "restapifrontoffice.reservauto.net";
        const PATH = `/api/v2/Rental/${rentalId}/FreeFloating`;

        const response = await fetch(
            `${LAMBDA_PROXY_URL}/?domain=${COMMUNAUTO_DOMAIN}&path=${encodeURIComponent(PATH)}`,
            {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${validToken}`
                },
            }
        );

        if (response.status === 401) {
            throw new Error('unauthorized');
        }

        if (!response.ok) {
            return { success: false, message: `Cancel failed: ${response.statusText}` };
        }

        return { success: true, message: 'Booking cancelled successfully!' };
    };

    return callApiWithRefresh(apiCancelBookingImpl, token, rentalId);
}

// Hardcoded list of all known Communauto car models
// Generated from API data - update periodically if new models are added
const ALL_CAR_MODELS = [
    "Bolt EUV",
    "Bolt EV",
    "Corolla",
    "Corolla Hatchback",
    "Corolla hybride",
    "Corolla L",
    "Elantra",
    "Elantra Hybride",
    "Forte",
    "Forte5",
    "ForteLX",
    "K4",
    "Kicks",
    "Kona",
    "Niro EV",
    "Niro hybride",
    "Prius C",
    "RAV4",
    "Rio",
    "Rio EX",
    "Seltos",
    "Sentra",
    "Sportage",
    "Trax",
    "Venue"
];

export async function getCarModels() {
    // Return the complete hardcoded list instead of fetching from API
    // This ensures all models are always available for filtering,
    // even when specific models have no available vehicles
    return ALL_CAR_MODELS;
}
